/**
 * Leave It Sorted — Homepage script
 * Two modes, controlled by section setting `enable_cart_drawer`:
 *  - OFF (default): tier button click adds to cart + redirects straight to /checkout
 *  - ON:            tier button click adds to cart + opens a slide-in drawer.
 *                   A floating action button (FAB) shows the cart count and
 *                   re-opens the drawer at any time.
 * Currency respected via cart.currency + Intl.NumberFormat (Shopify Markets).
 */

(function () {
  'use strict';

  const wrapper = document.querySelector('.lis-homepage');
  if (!wrapper) return;

  const DRAWER_ENABLED = wrapper.dataset.drawerEnabled === 'true';
  const t = window.LIS_TRANSLATIONS || {};

  // ============================================
  // SHARED: Smooth scroll on internal anchors
  // ============================================
  wrapper.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (e) => {
      const targetId = link.getAttribute('href');
      if (!targetId || targetId === '#') return;
      const target = document.querySelector(targetId);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (history.pushState) history.pushState(null, '', targetId);
    });
  });

  // ============================================
  // SHARED: Country personalization
  // Replaces "the UK, US, Canada, Australia and New Zealand"
  // with the visitor's own country when detected. Falls back to
  // the full multi-country string otherwise.
  // ============================================
  const COUNTRY_MAP = {
    GB: 'the UK',
    UK: 'the UK',
    US: 'the US',
    CA: 'Canada',
    AU: 'Australia',
    NZ: 'New Zealand',
  };

  function detectCountry() {
    // 1. Shopify Markets sets Shopify.country to the ISO code of the market.
    if (window.Shopify && typeof window.Shopify.country === 'string') {
      const c = window.Shopify.country.toUpperCase();
      if (COUNTRY_MAP[c]) return c;
    }
    // 2. Fall back to the browser locale, e.g. "en-GB" → "GB".
    const langs = (navigator.languages && navigator.languages.length)
      ? navigator.languages
      : [navigator.language || ''];
    for (let i = 0; i < langs.length; i++) {
      const parts = langs[i].split('-');
      if (parts.length > 1) {
        const c = parts[parts.length - 1].toUpperCase();
        if (COUNTRY_MAP[c]) return c;
      }
    }
    return null;
  }

  const detectedCountry = detectCountry();
  if (detectedCountry) {
    const label = COUNTRY_MAP[detectedCountry];
    wrapper.querySelectorAll('.lis-country').forEach((el) => {
      // If the element carries a data-single template, replace the whole
      // enclosing phrase (so words like "across" / "no jurisdiction lock-in"
      // that only make sense for the multi-country version disappear).
      // Otherwise, just swap the inner text — legacy behaviour.
      const tpl = el.getAttribute('data-single');
      if (tpl) {
        el.innerHTML = tpl.replace(/\{country\}/g, label);
      } else {
        el.textContent = label;
      }
    });
  }

  // ============================================
  // SHARED: FAQ accordion (single open at a time)
  // ============================================
  const faqItems = wrapper.querySelectorAll('.faq-item');
  faqItems.forEach((item) => {
    item.addEventListener('toggle', () => {
      if (item.open) {
        faqItems.forEach((other) => {
          if (other !== item && other.open) other.open = false;
        });
      }
    });
  });

  // ============================================
  // HELPERS
  // ============================================
  function escapeHtml(str) {
    if (typeof str !== 'string') return String(str == null ? '' : str);
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents, currency) {
    const lang = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(lang, {
        style: 'currency',
        currency: currency || 'EUR',
      }).format((cents || 0) / 100);
    } catch (e) {
      return `${((cents || 0) / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  }

  async function fetchCart() {
    const resp = await fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { 'Accept': 'application/json' },
    });
    if (!resp.ok) throw new Error('cart_fetch_failed');
    return await resp.json();
  }

  async function addToCart(variantId) {
    const resp = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.description || 'add_failed');
    }
    return await resp.json();
  }

  function setBusy(btn, busy, label) {
    if (busy) {
      btn.dataset.busy = '1';
      btn.dataset.originalHtml = btn.innerHTML;
      btn.setAttribute('aria-busy', 'true');
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.7';
      if (label) btn.innerHTML = label;
    } else {
      if (btn.dataset.originalHtml) btn.innerHTML = btn.dataset.originalHtml;
      btn.removeAttribute('aria-busy');
      btn.style.pointerEvents = '';
      btn.style.opacity = '';
      btn.dataset.busy = '0';
    }
  }

  // ============================================
  // MODE A — DIRECT CHECKOUT (default)
  // ============================================
  async function handleDirectCheckout(btn, e) {
    e.preventDefault();
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) {
      window.location.href = btn.getAttribute('href') || '/cart';
      return;
    }
    if (btn.dataset.busy === '1') return;
    setBusy(btn, true, t.processing || 'Processing…');
    try {
      await addToCart(variantId);
      window.location.href = window.Shopify.routes.root + 'checkout';
    } catch (err) {
      console.error('[LIS] Checkout error:', err);
      setBusy(btn, false);
      alert(t.error || 'Something went wrong. Please try again in a moment.');
    }
  }

  // ============================================
  // MODE B — DRAWER
  // ============================================
  const drawer = wrapper.querySelector('.lis-cart-drawer');
  const backdrop = wrapper.querySelector('.lis-cart-drawer-backdrop');
  const fab = wrapper.querySelector('.lis-cart-fab');
  const drawerBody = drawer && drawer.querySelector('.lis-cart-drawer-body');
  const drawerFooter = drawer && drawer.querySelector('.lis-cart-drawer-footer');
  const closeBtn = drawer && drawer.querySelector('.lis-cart-drawer-close');
  const fabBadge = fab && fab.querySelector('.lis-cart-fab-badge');

  let lastFocusedEl = null;

  function renderDrawer(cart) {
    if (!drawerBody || !drawerFooter) return;
    const currency = cart.currency || 'EUR';

    if (!cart.item_count || cart.item_count === 0) {
      drawerBody.innerHTML =
        '<div class="lis-cart-empty">' +
        (t.empty || 'Your cart is empty.') +
        '</div>';
      drawerFooter.style.display = 'none';
      return;
    }

    let itemsHtml = '';
    cart.items.forEach((item) => {
      const imgSrc = item.image || (item.featured_image && item.featured_image.url) || '';
      const productTitle = escapeHtml(item.product_title || item.title || '');
      const variant = escapeHtml(item.variant_title || '');
      const key = escapeHtml(item.key);
      itemsHtml +=
        '<div class="lis-cart-item" data-line-key="' + key + '">' +
          '<div class="lis-cart-item-image">' +
            (imgSrc ? '<img src="' + escapeHtml(imgSrc) + '" alt="' + productTitle + '" loading="lazy">' : '') +
          '</div>' +
          '<div class="lis-cart-item-info">' +
            '<div class="lis-cart-item-title">' + productTitle + '</div>' +
            (variant ? '<div class="lis-cart-item-variant">' + variant + '</div>' : '') +
            '<button class="lis-cart-item-remove" type="button" data-line-key="' + key + '">' + (t.remove || 'Remove') + '</button>' +
          '</div>' +
          '<div class="lis-cart-item-price">' + formatMoney(item.line_price, currency) + '</div>' +
        '</div>';
    });
    drawerBody.innerHTML = itemsHtml;

    const pmSrc = drawer && drawer.getAttribute('data-payment-methods-src');
    const paymentsHtml = pmSrc
      ? '<div class="lis-cart-payment-methods">' +
          '<img src="' + escapeHtml(pmSrc) + '" alt="Accepted payment methods — Apple Pay, Visa, Mastercard, American Express, McAfee Secure" loading="lazy">' +
        '</div>'
      : '';
    drawerFooter.innerHTML =
      '<div class="lis-cart-subtotal">' +
        '<span>' + (t.subtotal || 'Subtotal') + '</span>' +
        '<span class="lis-cart-subtotal-amount">' + formatMoney(cart.total_price, currency) + '</span>' +
      '</div>' +
      '<a href="' + window.Shopify.routes.root + 'checkout" class="lis-cart-checkout-btn">' +
        (t.checkout || 'Proceed to checkout — Instant Download') +
      '</a>' +
      paymentsHtml;
    drawerFooter.style.display = 'block';

    drawerBody.querySelectorAll('.lis-cart-item-remove').forEach((rmBtn) => {
      rmBtn.addEventListener('click', () => removeLine(rmBtn.dataset.lineKey));
    });
  }

  function updateFab(cart, pulse) {
    if (!fab || !fabBadge) return;
    if (cart && cart.item_count > 0) {
      fab.classList.add('is-visible');
      fabBadge.textContent = cart.item_count;
      fabBadge.style.display = 'flex';
      if (pulse) {
        fab.classList.remove('lis-cart-fab--pulse');
        void fab.offsetWidth; // reflow to restart animation
        fab.classList.add('lis-cart-fab--pulse');
        setTimeout(() => fab.classList.remove('lis-cart-fab--pulse'), 700);
      }
    } else {
      fab.classList.remove('is-visible');
    }
  }

  function openDrawer() {
    if (!drawer || !backdrop) return;
    lastFocusedEl = document.activeElement;
    drawer.classList.add('is-open');
    backdrop.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');
    setTimeout(() => { if (closeBtn) closeBtn.focus(); }, 100);
  }

  function closeDrawer() {
    if (!drawer || !backdrop) return;
    drawer.classList.remove('is-open');
    backdrop.classList.remove('is-open');
    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
    if (lastFocusedEl && lastFocusedEl.focus) lastFocusedEl.focus();
  }

  async function removeLine(lineKey) {
    try {
      const resp = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });
      const cart = await resp.json();
      renderDrawer(cart);
      updateFab(cart);
    } catch (e) {
      console.error('[LIS Cart] Remove failed:', e);
    }
  }

  async function handleAddToDrawer(btn, e) {
    e.preventDefault();
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;
    if (btn.dataset.busy === '1') return;
    setBusy(btn, true, t.adding || 'Adding…');
    try {
      await addToCart(variantId);
      const cart = await fetchCart();
      renderDrawer(cart);
      updateFab(cart, true);
      openDrawer();
    } catch (err) {
      console.error('[LIS Cart] Add failed:', err);
      alert(t.error || 'Something went wrong. Please try again.');
    } finally {
      setBusy(btn, false);
    }
  }

  // ============================================
  // WIRE TIER BUTTONS (delegates to correct mode)
  // ============================================
  wrapper.querySelectorAll('.js-lis-checkout').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      if (DRAWER_ENABLED) {
        handleAddToDrawer(btn, e);
      } else {
        handleDirectCheckout(btn, e);
      }
    });
  });

  // ============================================
  // DRAWER MODE — wire FAB, close button, backdrop, Esc key
  // ============================================
  if (DRAWER_ENABLED && drawer && fab) {
    fab.addEventListener('click', async () => {
      try {
        const cart = await fetchCart();
        renderDrawer(cart);
        openDrawer();
      } catch (e) {
        console.error('[LIS Cart] Fetch on FAB failed:', e);
      }
    });

    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (backdrop) backdrop.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    // Initial FAB state (in case cart already has items from a previous visit)
    fetchCart().then((cart) => updateFab(cart)).catch(() => {});
  }

  // ============================================
  // CROSS-SELL (inside cart drawer)
  // Reads product info from #lis-cross-sell-data (rendered by Liquid
  // only when both the drawer AND the cross-sell option are enabled).
  // ============================================
  const crossSellWrap = wrapper.querySelector('.lis-cart-cross-sell');
  const crossSellDataEl = document.getElementById('lis-cross-sell-data');
  let crossSellData = null;
  if (crossSellDataEl) {
    try { crossSellData = JSON.parse(crossSellDataEl.textContent); }
    catch (e) { crossSellData = null; }
  }

  function renderCrossSell(cart) {
    if (!crossSellWrap || !crossSellData) return;
    // Hide when cart is empty, or when the cross-sell is already in it.
    const inCart = (cart.items || []).some(
      (i) => parseInt(i.variant_id || i.id, 10) === parseInt(crossSellData.id, 10)
    );
    if (!cart.item_count || inCart) {
      crossSellWrap.style.display = 'none';
      crossSellWrap.setAttribute('aria-hidden', 'true');
      return;
    }
    const img = crossSellData.image
      ? '<img src="' + escapeHtml(crossSellData.image) + '" alt="' + escapeHtml(crossSellData.title) + '" loading="lazy">'
      : '';
    crossSellWrap.innerHTML =
      '<div class="lis-cart-cross-sell-title">You may also want:</div>' +
      '<div class="lis-cart-cross-sell-inner">' +
        '<div class="lis-cart-cross-sell-image">' + img + '</div>' +
        '<div>' +
          '<div class="lis-cart-cross-sell-name">' + escapeHtml(crossSellData.title) + '</div>' +
          '<div class="lis-cart-cross-sell-price">' + escapeHtml(crossSellData.price) + '</div>' +
        '</div>' +
        '<button class="lis-cart-cross-sell-btn" type="button" data-variant-id="' + crossSellData.id + '">Add</button>' +
      '</div>';
    crossSellWrap.style.display = 'block';
    crossSellWrap.setAttribute('aria-hidden', 'false');
    const addBtn = crossSellWrap.querySelector('.lis-cart-cross-sell-btn');
    if (addBtn) {
      addBtn.addEventListener('click', async () => {
        addBtn.setAttribute('disabled', 'disabled');
        addBtn.textContent = t.adding || 'Adding…';
        try {
          await addToCart(crossSellData.id);
          const fresh = await fetchCart();
          renderDrawer(fresh);
          renderCrossSell(fresh);
          updateFab(fresh, true);
        } catch (e) {
          console.error('[LIS Cart] Cross-sell add failed:', e);
          addBtn.removeAttribute('disabled');
          addBtn.textContent = 'Add';
        }
      });
    }
  }

  // Wrap the existing renderDrawer / removeLine so cross-sell stays in sync.
  if (crossSellWrap && crossSellData) {
    const _origRender = renderDrawer;
    renderDrawer = function (cart) {
      _origRender(cart);
      renderCrossSell(cart);
    };
  }

  // ============================================
  // IMAGE LIGHTBOX (used by carousel pages)
  // Injected into <body> so no ancestor transform/filter can trap the
  // position: fixed overlay. Preserves original aspect ratio via
  // object-fit: contain — never stretched.
  // ============================================
  let lightbox = document.querySelector('.lis-lightbox');
  if (!lightbox) {
    lightbox = document.createElement('div');
    lightbox.className = 'lis-lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-hidden', 'true');
    lightbox.setAttribute('aria-label', 'Full-size preview');
    lightbox.innerHTML =
      '<button class="lis-lightbox-close" type="button" aria-label="Close preview">✕</button>' +
      '<img class="lis-lightbox-img" src="" alt="">';
    document.body.appendChild(lightbox);
  }
  const lightboxImg = lightbox.querySelector('.lis-lightbox-img');
  const lightboxClose = lightbox.querySelector('.lis-lightbox-close');
  let lightboxLastFocus = null;

  function openLightbox(src, alt) {
    if (!src) return;
    lightboxLastFocus = document.activeElement;
    lightboxImg.src = src;
    lightboxImg.alt = alt || '';
    lightbox.classList.add('is-open');
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => lightboxClose.focus(), 50);
  }
  function closeLightbox() {
    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    lightboxImg.src = '';
    if (lightboxLastFocus && lightboxLastFocus.focus) lightboxLastFocus.focus();
  }
  lightbox.addEventListener('click', (e) => {
    // Close on click outside the image (backdrop or close button)
    if (e.target !== lightboxImg) closeLightbox();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && lightbox.classList.contains('is-open')) closeLightbox();
  });

  // Delegated click handler for carousel slides — works even if a specific
  // slide's own listener didn't attach (e.g. dynamically added blocks).
  // The per-carousel drag detector sets `_lisDidDrag` on the .pages-carousel
  // element so this handler can distinguish a tap from a swipe.
  document.addEventListener('click', (e) => {
    const slide = e.target.closest && e.target.closest('.pages-carousel-slide');
    if (!slide) return;
    const carousel = slide.closest('.pages-carousel');
    if (carousel && carousel._lisDidDrag) return;
    const img = slide.querySelector('img');
    if (!img) return;
    const src = img.currentSrc || img.src;
    if (!src) return;
    e.preventDefault();
    openLightbox(src, img.alt || 'Preview page');
  });

  // ============================================
  // PAGES CAROUSEL (What you actually get)
  // 3 visible on desktop, 1 on mobile. Arrow + drag + autoplay.
  // Clicking a slide (without dragging) opens it in the lightbox.
  // ============================================
  wrapper.querySelectorAll('.pages-carousel').forEach((carousel) => {
    const viewport = carousel.querySelector('.pages-carousel-viewport');
    const track = carousel.querySelector('.pages-carousel-track');
    const slides = track ? track.querySelectorAll('.pages-carousel-slide') : [];
    const prevBtn = carousel.querySelector('.pages-carousel-arrow.prev');
    const nextBtn = carousel.querySelector('.pages-carousel-arrow.next');
    if (!viewport || !track || slides.length === 0) return;

    let index = 0;
    let autoplayTimer = null;
    const autoplayMs = parseInt(carousel.dataset.autoplay || '2000', 10);

    function visibleCount() {
      return window.matchMedia('(max-width: 720px)').matches ? 1 : 3;
    }

    function maxIndex() {
      return Math.max(0, slides.length - visibleCount());
    }

    function slideStep() {
      // Distance between slide starts = slide width + gap
      if (slides.length < 2) return viewport.clientWidth;
      return slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left;
    }

    function update(animate) {
      const offset = -1 * index * slideStep();
      track.style.transition = animate === false ? 'none' : '';
      track.style.transform = 'translate3d(' + offset + 'px, 0, 0)';
    }

    function goTo(i) {
      const max = maxIndex();
      if (i < 0) i = max;
      if (i > max) i = 0;
      index = i;
      update(true);
    }

    function startAutoplay() {
      stopAutoplay();
      if (autoplayMs <= 0) return;
      autoplayTimer = setInterval(() => goTo(index + 1), autoplayMs);
    }
    function stopAutoplay() {
      if (autoplayTimer) { clearInterval(autoplayTimer); autoplayTimer = null; }
    }

    if (prevBtn) prevBtn.addEventListener('click', () => { goTo(index - 1); startAutoplay(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { goTo(index + 1); startAutoplay(); });

    // Pause on hover
    carousel.addEventListener('mouseenter', stopAutoplay);
    carousel.addEventListener('mouseleave', startAutoplay);
    // Pause when tab hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay(); else startAutoplay();
    });

    // Drag / swipe support — with click-vs-drag detection so a small,
    // stationary press falls through to the delegated slide-click handler
    // (which opens the lightbox), while a real swipe pages the carousel.
    let dragStartX = 0;
    let dragCurrentX = 0;
    let isDragging = false;
    let pointerId = null;
    carousel._lisDidDrag = false;
    const DRAG_TAP_THRESHOLD = 6; // px — under this = treat as click

    function onDown(e) {
      isDragging = true;
      carousel._lisDidDrag = false;
      pointerId = e.pointerId;
      dragStartX = e.clientX;
      dragCurrentX = e.clientX;
      track.classList.add('is-dragging');
      stopAutoplay();
      if (track.setPointerCapture) {
        try { track.setPointerCapture(pointerId); } catch (_) {}
      }
    }
    function onMove(e) {
      if (!isDragging) return;
      dragCurrentX = e.clientX;
      const delta = dragCurrentX - dragStartX;
      if (Math.abs(delta) > DRAG_TAP_THRESHOLD) carousel._lisDidDrag = true;
      const baseOffset = -1 * index * slideStep();
      track.style.transition = 'none';
      track.style.transform = 'translate3d(' + (baseOffset + delta) + 'px, 0, 0)';
    }
    function onUp() {
      if (!isDragging) return;
      const delta = dragCurrentX - dragStartX;
      isDragging = false;
      track.classList.remove('is-dragging');
      const threshold = slideStep() * 0.2;
      if (carousel._lisDidDrag && delta > threshold) goTo(index - 1);
      else if (carousel._lisDidDrag && delta < -threshold) goTo(index + 1);
      else update(true);
      startAutoplay();
      // Clear the drag flag on the next tick so the click event that
      // follows sees the current pointerup's state, but subsequent taps
      // start clean.
      setTimeout(() => { carousel._lisDidDrag = false; }, 0);
    }

    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);
    track.addEventListener('pointerleave', onUp);
    // Native browser drag on images would fight our pointer logic.
    track.addEventListener('dragstart', (e) => e.preventDefault());

    // Keyboard accessibility: focusable slides that open the lightbox on
    // Enter / Space (the mouse click path is handled by the document-level
    // delegated listener above).
    slides.forEach((slide) => {
      slide.setAttribute('role', 'button');
      slide.setAttribute('tabindex', '0');
      slide.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const img = slide.querySelector('img');
          if (img) openLightbox(img.currentSrc || img.src, img.alt || 'Preview page');
        }
      });
    });

    // Re-layout on resize / after images load
    window.addEventListener('resize', () => {
      const max = maxIndex();
      if (index > max) index = max;
      update(false);
    });
    window.addEventListener('load', () => update(false));
    update(false);
    startAutoplay();
  });
})();
